/**
 * Le job qu'une page a laissé derrière elle.
 *
 * Une génération hébergée est durable côté serveur — une ligne en base, un
 * journal d'événements, un artefact signé — mais la page ne la connaissait
 * qu'à travers une promesse gardée en mémoire. Sur un téléphone, quatre
 * minutes de génération veulent dire un écran qui se verrouille : iOS gèle
 * alors les minuteurs de l'onglet en arrière-plan et peut le jeter. La page
 * se recharge sans savoir qu'un job tourne, et l'image qui arrive n'est
 * jamais montrée.
 *
 * Constaté en production le 2026-09-28, sur la première image que le
 * propriétaire ait lancée depuis son téléphone : le job a réussi en 3 min
 * 56 s, les quarante événements du serveur vont de 3,3 % à 100 %, et son
 * écran n'a jamais changé.
 *
 * On écrit donc l'identifiant du job, et la page le reprend au chargement.
 * `localStorage` peut lever (navigation privée, données de site bloquées) :
 * chaque accès est gardé, et une page qui ne peut pas se souvenir se comporte
 * exactement comme avant.
 */
import type { GenerationJobKind } from "../types/index.js";

const PENDING_JOB_KEY = "image-generator.pending-job";

export interface PendingJob {
	job_id: string;
	kind: GenerationJobKind;
	/** La machine à qui il a été confié, pour savoir si elle répond encore. */
	worker_id?: string | null;
}

export function rememberPendingJob(pending: PendingJob): void {
	try {
		localStorage.setItem(PENDING_JOB_KEY, JSON.stringify(pending));
	} catch {
		// Pas de mémoire : on perd la reprise, pas la génération.
	}
}

export function forgetPendingJob(): void {
	try {
		localStorage.removeItem(PENDING_JOB_KEY);
	} catch {
		// Idem : rien à faire, et surtout rien à interrompre.
	}
}

/** Le job noté, s'il y en a un et s'il est lisible. */
export function readPendingJob(): PendingJob | null {
	let raw: string | null = null;
	try {
		raw = localStorage.getItem(PENDING_JOB_KEY);
	} catch {
		return null;
	}
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!parsed || typeof parsed !== "object") return null;
		const { job_id, kind, worker_id } = parsed as Record<string, unknown>;
		// Une entrée écrite par une version plus ancienne, ou tronquée, ne doit
		// pas faire échouer le démarrage de la page.
		if (typeof job_id !== "string" || !job_id || typeof kind !== "string") return null;
		return {
			job_id,
			kind: kind as GenerationJobKind,
			worker_id: typeof worker_id === "string" ? worker_id : null,
		};
	} catch {
		forgetPendingJob();
		return null;
	}
}
